import {
    calculateEffectiveWorkedMinutes,
    getLogsForCalendarDay,
    sortEmployeeDayLogs
} from "./employeeDayEffectiveMinutes"

const timeToMinutes = (time) => {
    if (!time || typeof time !== "string") return 0
    const [hours, minutes] = time.split(":").map(Number)
    if (Number.isNaN(hours)) return 0
    return hours * 60 + (Number.isNaN(minutes) ? 0 : minutes)
}

/** 8h po danu sa prijavom — isto kao predviđeni izlazak u Prisutnosti. */
export const EMPLOYEE_MONTH_TARGET_MINUTES_PER_DAY = 8 * 60

/** Maksimalno uračunata pauza po danu (kao u helpers.js). */
export const EMPLOYEE_MONTH_MAX_PAUSE_CREDIT_MINUTES = 30

/**
 * Pauza = vreme između izlaza i sledećeg ulaza (logika kao calculateUserLogs u helpers.js).
 * Uračunava se samo ako je taj dan korišćena pauza (≥2 ulaza).
 */
export const calculateDayPauseCreditMinutes = (dayLogs) => {
    if (!dayLogs?.length) return 0

    const userLogs = sortEmployeeDayLogs(dayLogs)
    const ulazCount = userLogs.filter((l) => l.status === "ulaz").length
    if (ulazCount < 2) return 0

    const firstEntryIndex = userLogs.findIndex((log) => log.status === "ulaz")
    if (firstEntryIndex === -1) return 0

    let pauseDuration = 0

    for (let i = firstEntryIndex + 1; i < userLogs.length - 1; i += 2) {
        const startPauseMin = timeToMinutes(userLogs[i].vreme)
        const endPauseMin = timeToMinutes(userLogs[i + 1].vreme)
        pauseDuration +=
            endPauseMin >= startPauseMin
                ? endPauseMin - startPauseMin
                : 1440 - startPauseMin + endPauseMin
    }

    return Math.min(pauseDuration, EMPLOYEE_MONTH_MAX_PAUSE_CREDIT_MINUTES)
}

/**
 * @returns {{
 *   attendanceDays: number,
 *   workedMinutes: number,
 *   pauseCreditMinutes: number,
 *   creditedMinutes: number,
 *   expectedMinutes: number,
 *   differenceMinutes: number,
 *   status: "none"|"exact"|"over"|"short"
 * }}
 */
export const calculateEmployeeMonthWorkSummary = (logsByDay, year, monthIndex0) => {
    const daysInMonth = new Date(year, monthIndex0 + 1, 0).getDate()
    const now = new Date()
    const todayYear = now.getFullYear()
    const todayMonth = now.getMonth()
    const todayDate = now.getDate()
    let attendanceDays = 0
    let workedMinutes = 0
    let pauseCreditMinutes = 0

    for (let day = 1; day <= daysInMonth; day++) {
        if (year === todayYear && monthIndex0 === todayMonth && day === todayDate) continue

        const dayLogs = getLogsForCalendarDay(logsByDay, year, monthIndex0, day)
        const hasUlaz = dayLogs.some((l) => l.status === "ulaz")
        if (!hasUlaz) continue

        attendanceDays += 1
        workedMinutes += calculateEffectiveWorkedMinutes(dayLogs) ?? 0
        pauseCreditMinutes += calculateDayPauseCreditMinutes(dayLogs)
    }

    const creditedMinutes = workedMinutes + pauseCreditMinutes
    const expectedMinutes = attendanceDays * EMPLOYEE_MONTH_TARGET_MINUTES_PER_DAY
    const differenceMinutes = creditedMinutes - expectedMinutes

    if (attendanceDays === 0) {
        return {
            attendanceDays: 0,
            workedMinutes: 0,
            pauseCreditMinutes: 0,
            creditedMinutes: 0,
            expectedMinutes: 0,
            differenceMinutes: 0,
            status: "none"
        }
    }

    let status = "exact"
    if (differenceMinutes > 0) status = "over"
    else if (differenceMinutes < 0) status = "short"

    return {
        attendanceDays,
        workedMinutes,
        pauseCreditMinutes,
        creditedMinutes,
        expectedMinutes,
        differenceMinutes,
        status
    }
}

export const formatMonthWorkHoursMinutes = (totalMinutes) => {
    const safe = Math.max(0, Math.round(totalMinutes))
    const h = Math.floor(safe / 60)
    const m = safe % 60
    return `${h}:${String(m).padStart(2, "0")}`
}

/**
 * @returns {{ shortLine: string, detailLines: string[], status: string }}
 */
export const buildEmployeeMonthWorkSummaryText = (summary) => {
    if (summary.status === "none") {
        return {
            shortLine: "Zaposleni nije radio u izabranom mesecu.",
            detailLines: [],
            status: "none"
        }
    }

    const dayWord = summary.attendanceDays === 1 ? "dan" : "dana"
    const detailLines = [
        `Predviđeno za mesec (${summary.attendanceDays} ${dayWord} sa prijavom × 8h): ${formatMonthWorkHoursMinutes(summary.expectedMinutes)}`,
        `Odrađeno ukupno (rad + pauza do 30 min/dan): ${formatMonthWorkHoursMinutes(summary.creditedMinutes)}`,
        `Razlika: ${formatMonthWorkHoursMinutes(Math.abs(summary.differenceMinutes))}${summary.status === "over" ? " prekovremeno" : summary.status === "short" ? " kraće" : " — tačno"}`
    ]

    let shortLine
    if (summary.status === "exact") {
        shortLine = "Zaposleni je za izabrani mesec, radio tačno predviđeno vreme."
    } else if (summary.status === "over") {
        shortLine = `Zaposleni je za izabrani mesec, radio ${formatMonthWorkHoursMinutes(summary.differenceMinutes)} prekovremeno.`
    } else {
        shortLine = `Zaposleni je za izabrani mesec, radio ${formatMonthWorkHoursMinutes(Math.abs(summary.differenceMinutes))} kraće.`
    }

    return {
        shortLine,
        detailLines,
        status: summary.status
    }
}
